<template>
  <div>
    <vx-card>
      <div class="flex justify-between items-center mb-4">
        <h2>HaioFlash VMs (Admin View)</h2>
        <vs-button color="success" @click="loadVms()">
          <feather-icon icon="RefreshCwIcon" class="mr-2"></feather-icon>
          Refresh
        </vs-button>
      </div>
      
      <!-- Filters -->
      <vs-row class="mb-4">
        <vs-col vs-w="3" vs-sm="6" vs-xs="12">
          <vs-input
            v-model="filters.vm_id"
            class="w-full"
            label-placeholder="VM ID"
            @keyup.enter="loadVms()"
          ></vs-input>
        </vs-col>
        <vs-col vs-w="3" vs-sm="6" vs-xs="12">
          <vs-input
            v-model="filters.user_id"
            class="w-full"
            label-placeholder="User ID"
            @keyup.enter="loadVms()"
          ></vs-input>
        </vs-col>
        <vs-col vs-w="3" vs-sm="6" vs-xs="12">
          <vs-select
            v-model="filters.status"
            class="w-full"
            placeholder="Status"
            @change="loadVms()"
          >
            <vs-select-item value="" text="All"></vs-select-item>
            <vs-select-item value="running" text="Running"></vs-select-item>
            <vs-select-item value="stopped" text="Stopped"></vs-select-item>
            <vs-select-item value="creating" text="Creating"></vs-select-item>
            <vs-select-item value="error" text="Error"></vs-select-item>
          </vs-select>
        </vs-col>
        <vs-col vs-w="2" vs-sm="12" vs-xs="12">
          <vs-button color="primary" class="w-full" @click="loadVms()">
            <feather-icon icon="SearchIcon"></feather-icon>
          </vs-button>
        </vs-col>
      </vs-row>
      
      <!-- VMs Table -->
      <vs-table v-if="vms" :data="vms">
        <template slot="thead">
          <vs-th>VM ID</vs-th>
          <vs-th>User</vs-th>
          <vs-th>Plan</vs-th>
          <vs-th>Region</vs-th>
          <vs-th>Node</vs-th>
          <vs-th>IP Address</vs-th>
          <vs-th>IPv4 Addon</vs-th>
          <vs-th>Resources</vs-th>
          <vs-th>Memory Usage</vs-th>
          <vs-th>Bandwidth</vs-th>
          <vs-th>User Balance</vs-th>
          <vs-th>Status</vs-th>
          <vs-th>Created</vs-th>
          <vs-th>Actions</vs-th>
        </template>
        
        <template slot-scope="{ data }">
          <vs-tr :key="indextr" v-for="(tr, indextr) in data">
            <vs-td>{{ tr.microvm_id }}</vs-td>
            <vs-td>
              <div>
                <vs-button
                  size="small"
                  color="primary"
                  type="flat"
                  icon="account_box"
                  :to="`/user?id=${tr.user_id}`"
                  style="color: white !important;"
                >
                  {{ tr.user_name }}
                </vs-button>
                <br>
                <small class="text-grey">{{ tr.user_email }}</small>
              </div>
            </vs-td>
            <vs-td>{{ tr.plan_name }}</vs-td>
            <vs-td>{{ tr.region_name }}</vs-td>
            <vs-td>{{ tr.node_name || 'N/A' }}</vs-td>
            <vs-td>{{ tr.ipv4_address || 'Pending' }}</vs-td>
            <vs-td>
              <div v-if="tr.ipv4_addon">
                <vs-chip :color="tr.ipv4_addon.status === 'active' ? 'success' : 'warning'" size="small">
                  {{ tr.ipv4_addon.ipv4_address }}
                </vs-chip>
                <br>
                <small class="text-grey">
                  {{ tr.ipv4_addon.price_monthly > 0 ? (tr.ipv4_addon.price_monthly / 1000).toFixed(0) + 'K IRR/mo' : 'Free' }}
                </small>
              </div>
              <small v-else class="text-grey">No addon</small>
            </vs-td>
            <vs-td>
              <small>
                <strong>CPU:</strong> {{ tr.vcpus }} vCPU<br>
                <strong>RAM:</strong> {{ tr.memory_base_mb }}{{ tr.memory_burst_mb > tr.memory_base_mb ? ' - ' + tr.memory_burst_mb : '' }} MB<br>
                <strong>Disk:</strong> {{ tr.storage_gb }} GB
              </small>
            </vs-td>
            <vs-td>
              <div class="memory-cell">
                <div class="flex justify-between mb-1">
                  <small><strong>{{ tr.memory_used_mb || 0 }} MB</strong> / {{ tr.memory_plan_mb }} MB</small>
                  <small class="text-grey">{{ tr.memory_usage_percent || 0 }}%</small>
                </div>
                <vs-progress 
                  :percent="tr.memory_usage_percent || 0" 
                  :color="getMemoryColor(tr.memory_usage_percent)"
                ></vs-progress>
                <small class="text-grey">CPU: {{ (tr.cpu_usage_percent || 0).toFixed(1) }}%</small>
              </div>
            </vs-td>
            <vs-td>
              <div class="bandwidth-cell">
                <div class="flex justify-between mb-1">
                  <small><strong>{{ tr.bandwidth_used_gb || 0 }} GB</strong> / {{ tr.bandwidth_quota_gb }} GB</small>
                  <small class="text-grey">{{ tr.bandwidth_usage_percent || 0 }}%</small>
                </div>
                <vs-progress 
                  :percent="tr.bandwidth_usage_percent || 0" 
                  :color="getBandwidthColor(tr.bandwidth_usage_percent)"
                ></vs-progress>
                <small class="text-grey">{{ tr.bandwidth_remaining_gb || tr.bandwidth_quota_gb }} GB left</small>
              </div>
            </vs-td>
            <vs-td>
              <vs-chip :color="getBalanceColor(tr.user_balance)">
                ${{ parseFloat(tr.user_balance || 0).toFixed(2) }}
              </vs-chip>
            </vs-td>
            <vs-td>
              <vs-chip :color="getStatusColor(tr.status)">
                {{ tr.status }}
              </vs-chip>
            </vs-td>
            <vs-td>{{ formatDate(tr.created_at) }}</vs-td>
            <vs-td>
              <vs-dropdown vs-custom-content vs-trigger-click>
                <vs-button
                  size="small"
                  type="filled"
                  icon="more_vert"
                ></vs-button>
                
                <vs-dropdown-menu>
                  <vs-dropdown-item @click="viewVmDetails(tr)">
                    <feather-icon icon="EyeIcon" class="mr-2" svgClasses="h-4 w-4"></feather-icon>
                    View Details
                  </vs-dropdown-item>
                  <vs-dropdown-item @click="showMigrateDialog(tr)">
                    <feather-icon icon="ArrowRightIcon" class="mr-2" svgClasses="h-4 w-4"></feather-icon>
                    Migrate VM
                  </vs-dropdown-item>
                  <vs-dropdown-item @click="deleteVm(tr.id)">
                    <feather-icon icon="TrashIcon" class="mr-2" svgClasses="h-4 w-4"></feather-icon>
                    <span class="text-danger">Delete VM</span>
                  </vs-dropdown-item>
                </vs-dropdown-menu>
              </vs-dropdown>
            </vs-td>
          </vs-tr>
        </template>
      </vs-table>
      
      <vs-pagination
        class="my-3"
        :total="pagination.last_page"
        v-model="pagination.current_page"
        @change="loadVms()"
      ></vs-pagination>
    </vx-card>
    
    <!-- VM Details Dialog -->
    <vs-popup
      title="VM Details"
      :active.sync="showDetailsDialog"
    >
      <div class="p-4" v-if="selectedVm">
        <vs-row>
          <vs-col vs-w="6" class="mb-3">
            <strong>VM ID:</strong> {{ selectedVm.microvm_id }}
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>Status:</strong>
            <vs-chip :color="getStatusColor(selectedVm.status)">
              {{ selectedVm.status }}
            </vs-chip>
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>User:</strong> {{ selectedVm.user_name }}
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>Email:</strong> {{ selectedVm.user_email }}
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>User Balance:</strong> 
            <vs-chip :color="getBalanceColor(selectedVm.user_balance)">
              ${{ parseFloat(selectedVm.user_balance || 0).toFixed(2) }}
            </vs-chip>
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>Plan:</strong> {{ selectedVm.plan_name }}
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>Region:</strong> {{ selectedVm.region_name }}
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>Node:</strong> {{ selectedVm.node_name || 'N/A' }}
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>IPv4 Address:</strong> {{ selectedVm.ipv4_address || 'Pending' }}
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>IPv4 Addon:</strong>
            <div v-if="selectedVm.ipv4_addon">
              <vs-chip :color="selectedVm.ipv4_addon.status === 'active' ? 'success' : 'warning'" size="small">
                {{ selectedVm.ipv4_addon.ipv4_address }}
              </vs-chip>
              <br>
              <small>
                Price: {{ selectedVm.ipv4_addon.price_monthly > 0 ? (selectedVm.ipv4_addon.price_monthly / 1000).toFixed(0) + 'K IRR/month' : 'Free' }}
              </small>
              <br>
              <small>Status: {{ selectedVm.ipv4_addon.status }}</small>
              <br>
              <small>Next Billing: {{ selectedVm.ipv4_addon.next_billing_date || 'N/A' }}</small>
            </div>
            <span v-else class="text-grey">No addon</span>
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>IPv6 Address:</strong> {{ selectedVm.ipv6_address || 'N/A' }}
          </vs-col>
          <vs-col vs-w="12" class="mb-3">
            <strong>Resources:</strong>
            <ul class="ml-4">
              <li>vCPU: {{ selectedVm.vcpus }}</li>
              <li>Memory: {{ selectedVm.memory_base_mb }} MB (Base) / {{ selectedVm.memory_burst_mb }} MB (Burst)</li>
              <li>Storage: {{ selectedVm.storage_gb }} GB</li>
              <li>Bandwidth Speed: {{ selectedVm.bandwidth_speed_limit || 'Unlimited' }}</li>
            </ul>
          </vs-col>
          <vs-col vs-w="12" class="mb-3">
            <strong>Current Usage:</strong>
            <div class="mt-2">
              <div class="mb-3">
                <div class="flex justify-between mb-1">
                  <span>Memory: {{ selectedVm.memory_used_mb || 0 }} MB / {{ selectedVm.memory_plan_mb }} MB</span>
                  <span>{{ selectedVm.memory_usage_percent || 0 }}%</span>
                </div>
                <vs-progress 
                  :percent="selectedVm.memory_usage_percent || 0" 
                  :color="getMemoryColor(selectedVm.memory_usage_percent)"
                ></vs-progress>
              </div>
              <div class="mb-2">
                <div class="flex justify-between mb-1">
                  <span>CPU Usage</span>
                  <span>{{ (selectedVm.cpu_usage_percent || 0).toFixed(1) }}%</span>
                </div>
                <vs-progress 
                  :percent="selectedVm.cpu_usage_percent || 0" 
                  :color="getMemoryColor(selectedVm.cpu_usage_percent)"
                ></vs-progress>
              </div>
            </div>
          </vs-col>
          <vs-col vs-w="12" class="mb-3">
            <strong>Bandwidth Usage:</strong>
            <div class="mt-2">
              <div class="flex justify-between mb-1">
                <span>{{ selectedVm.bandwidth_used_gb || 0 }} GB / {{ selectedVm.bandwidth_quota_gb }} GB</span>
                <span>{{ selectedVm.bandwidth_usage_percent || 0 }}%</span>
              </div>
              <vs-progress 
                :percent="selectedVm.bandwidth_usage_percent || 0" 
                :color="getBandwidthColor(selectedVm.bandwidth_usage_percent)"
              ></vs-progress>
              <small class="text-grey">{{ selectedVm.bandwidth_remaining_gb || selectedVm.bandwidth_quota_gb }} GB remaining</small>
            </div>
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>Auto-Scale:</strong> 
            <vs-chip :color="selectedVm.autoscale_enabled ? 'success' : 'dark'">
              {{ selectedVm.autoscale_enabled ? 'Enabled' : 'Disabled' }}
            </vs-chip>
          </vs-col>
          <vs-col vs-w="6" class="mb-3" v-if="selectedVm.autoscale_enabled">
            <strong>Auto-Scale Range:</strong> {{ selectedVm.autoscale_min_mb }} - {{ selectedVm.autoscale_max_mb }} MB
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>Monthly Price:</strong> ${{ parseFloat(selectedVm.monthly_price || 0).toFixed(2) }}
          </vs-col>
          <vs-col vs-w="6" class="mb-3" v-if="selectedVm.autoscale_addon_price > 0">
            <strong>Auto-Scale Addon:</strong> ${{ parseFloat(selectedVm.autoscale_addon_price || 0).toFixed(2) }}/mo
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>Next Billing:</strong> {{ formatDate(selectedVm.next_billing_date) }}
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>Created:</strong> {{ formatDate(selectedVm.created_at) }}
          </vs-col>
          <vs-col vs-w="6" class="mb-3">
            <strong>Updated:</strong> {{ formatDate(selectedVm.updated_at) }}
          </vs-col>
        </vs-row>
      </div>
    </vs-popup>
    
    <!-- Migrate Dialog -->
    <vs-popup
      title="Migrate VM"
      :active.sync="showMigrate"
    >
      <div class="p-4">
        <p class="mb-4">Select target node for migration:</p>
        <vs-select
          v-model="migrateData.target_node_id"
          class="w-full mb-4"
          label="Target Node"
        >
          <vs-select-item
            v-for="node in availableNodes"
            :key="node.id"
            :value="node.id"
            :text="`${node.name} (${node.region_name})`"
          ></vs-select-item>
        </vs-select>
        
        <div class="flex justify-end">
          <vs-button
            color="danger"
            type="border"
            @click="showMigrate = false"
            class="mr-2"
          >
            Cancel
          </vs-button>
          <vs-button
            color="primary"
            @click="migrateVm()"
          >
            Migrate
          </vs-button>
        </div>
      </div>
    </vs-popup>
  </div>
</template>

<script>
export default {
  name: 'VmsList',
  data() {
    return {
      vms: [],
      availableNodes: [],
      showDetailsDialog: false,
      showMigrate: false,
      selectedVm: null,
      filters: {
        vm_id: '',
        user_id: '',
        status: ''
      },
      pagination: {
        current_page: 1,
        last_page: 1
      },
      migrateData: {
        vm_id: null,
        target_node_id: null
      }
    }
  },
  mounted() {
    this.loadVms()
    this.loadNodes()
  },
  methods: {
    async loadVms() {
      try {
        const params = {
          page: this.pagination.current_page,
          ...this.filters
        }
        const response = await this.$http.get('/admin/haioflash/vms', { params })
        
        // Debug logging
        console.log('VMs API Response:', response.data)
        console.log('Response success:', response.data.success)
        console.log('Response data:', response.data.data)
        
        if (response && response.data && response.data.success && response.data.data) {
          const paginatedData = response.data.data
          this.vms = Array.isArray(paginatedData.data) ? paginatedData.data : []
          this.pagination = {
            current_page: paginatedData.current_page || 1,
            last_page: paginatedData.last_page || 1
          }
          
          console.log('VMs loaded:', this.vms.length, this.vms)
        } else {
          this.vms = []
        }
      } catch (error) {
        console.error('Error loading VMs:', error)
        console.error('Error response:', error.response)
        this.vms = []
        this.$vs.notify({
          title: 'Error',
          text: 'Failed to load VMs',
          color: 'danger'
        })
        console.error(error)
      }
    },
    
    async loadNodes() {
      try {
        const response = await this.$http.get('/admin/haioflash/nodes')
        const data = response.data || {}
        this.availableNodes = data.data || []
      } catch (error) {
        this.availableNodes = []
        console.error('Failed to load nodes', error)
      }
    },
    
    viewVmDetails(vm) {
      this.selectedVm = vm
      this.showDetailsDialog = true
    },
    
    showMigrateDialog(vm) {
      this.migrateData.vm_id = vm.id
      this.showMigrate = true
    },
    
    async migrateVm() {
      if (!this.migrateData.target_node_id) {
        this.$vs.notify({
          title: 'Error',
          text: 'Please select a target node',
          color: 'warning'
        })
        return
      }
      
      try {
        this.$vs.loading()
        await this.$http.post(
          `/admin/haioflash/vms/${this.migrateData.vm_id}/migrate`,
          { target_node_id: this.migrateData.target_node_id }
        )
        this.$vs.loading.close()
        this.$vs.notify({
          title: 'Success',
          text: 'VM migration started',
          color: 'success'
        })
        this.showMigrate = false
        this.migrateData = { vm_id: null, target_node_id: null }
        this.loadVms()
      } catch (error) {
        this.$vs.loading.close()
        this.$vs.notify({
          title: 'Error',
          text: (error.response && error.response.data && error.response.data.message) || 'Failed to migrate VM',
          color: 'danger'
        })
      }
    },
    
    async deleteVm(id) {
      this.$vs.dialog({
        type: 'confirm',
        color: 'danger',
        title: 'Confirm Delete',
        text: 'Are you sure you want to delete this VM? This action cannot be undone.',
        accept: async () => {
          try {
            this.$vs.loading()
            await this.$http.delete(`/admin/haioflash/vms/${id}`)
            this.$vs.loading.close()
            this.$vs.notify({
              title: 'Success',
              text: 'VM deleted successfully',
              color: 'success'
            })
            this.loadVms()
          } catch (error) {
            this.$vs.loading.close()
            this.$vs.notify({
              title: 'Error',
              text: 'Failed to delete VM',
              color: 'danger'
            })
          }
        }
      })
    },
    
    getStatusColor(status) {
      const colors = {
        'running': 'success',
        'stopped': 'warning',
        'creating': 'primary',
        'error': 'danger',
        'stopping': 'warning'
      }
      return colors[status] || 'dark'
    },
    
    getBandwidthColor(percent) {
      if (percent >= 90) return 'danger'
      if (percent >= 75) return 'warning'
      return 'success'
    },
    
    getMemoryColor(percent) {
      if (percent >= 90) return 'danger'
      if (percent >= 80) return 'warning'
      return 'success'
    },
    
    getBalanceColor(balance) {
      if (balance <= 0) return 'danger'
      if (balance < 10) return 'warning'
      return 'success'
    },
    
    formatDate(date) {
      if (!date) return 'N/A'
      return new Date(date).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    }
  }
}
</script>

<style scoped>
.bandwidth-cell {
  min-width: 180px;
}

.bandwidth-cell small {
  font-size: 11px;
}

.memory-cell {
  min-width: 160px;
}

.memory-cell small {
  font-size: 11px;
}

.text-grey {
  color: #999;
}

/* Dark theme text visibility */
.theme-dark .vs-table tr td {
  color: #c2c6dc !important;
}

.theme-dark .vs-table tr td small {
  color: #888 !important;
}

.theme-dark .vs-table tr td strong {
  color: #e0e4f0 !important;
}
</style>
